import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

// project-imports
import { getFormattedDate } from 'helpers/dates';
import { isEmptyObject } from 'helpers/objectFunctions';
import { devConsolelog } from 'helpers/utilityFunctions';
import { dispatch, useSelector } from 'store';
import { rowsPerPagePaginationAlt } from 'store/constant';
import { getAcmRequirementsData } from 'store/slices/acmDashboard';
import {
    getMemberRequirementEditableData,
    getMemberRequirementsFilter,
    getMemberStats,
    getRequirementsData,
    patchCurrentRequirementData
} from 'store/slices/memberPriority';
import { openSnackbar } from 'store/slices/snackbar';
import MainCard from 'ui-component/cards/MainCard';
import SubCard from 'ui-component/cards/SubCard';
import {
    DASHBOARD_VIEW,
    MODULE_ACCESS_MEMBER_PRIORITY_DASHBOARD_SIDE_NAV,
    REQUIREMENTS_TYPE,
    REQUIREMENT_DURATION_FILTER_OPTIONS,
    REQUIREMENT_EDITABLE_FIELDS,
    durationFilterData,
    requirementHeadCells,
    MEMBER_LIST_VIEW,
    VIEW_TYPE_MPD_VIEW
} from '../constant';
import useStyles from '../styles';

// material-ui
import EditIcon from '@mui/icons-material/Edit';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import useTheme from '@mui/material/styles/useTheme';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// third-party
import { format } from 'date-fns';

const TableHeading = () => {
    const classes = useStyles();

    return (
        <>
            {requirementHeadCells.map((item) => (
                <TableCell
                    align={item.align}
                    sx={{ pl: 4, minWidth: '80px' }}
                    className={item.id === 'reqCode' && `${classes.primaryBackground} ${classes.stickyTableRow}`}
                >
                    <Grid container item alignItems="center" sx={{ flexWrap: 'nowrap !important' }}>
                        <Grid item>{item?.label}</Grid>
                        <Grid item>
                            {REQUIREMENT_EDITABLE_FIELDS.includes(item?.id) && (
                                <IconButton color="primary">
                                    <EditIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>
                </TableCell>
            ))}
        </>
    );
};

const Requirements = ({ viewType, handleDrawerOpen }) => {
    const theme = useTheme();
    const classes = useStyles();

    const memberPriorityData = useSelector((state) => state?.memberPriority);
    const acmData = useSelector((state) => state?.acmDashboard);

    const {
        selectedMember = {},
        requirementsData = {},
        memberRequirements = {},
        editableRequirementData = {},
        memberRequirementsFilter = {},
        isCountLoading = true,
        isRequirementDataLoading = true
    } = memberPriorityData || {};

    const { created_at: createdAt = '' } = memberRequirementsFilter || {};

    const { code: memberCode = '' } = selectedMember || {};

    const { requirements = {} } = memberRequirements || {};
    const {
        flight: flightRequirements = {},
        hotel: hotelRequirements = {},
        holidays: holidayRequirements = {},
        count: allRequirementsCount = ''
    } = requirements || {};
    const { count: flightRequirementCount = '' } = flightRequirements || {};
    const { count: hotelRequirementCount = '' } = hotelRequirements || {};
    const { count: holidayRequirementCount = '' } = holidayRequirements || {};

    const {
        results = {},
        count = 0,
        next = null,
        previous = null,
        page_size: rowsPerPage = rowsPerPagePaginationAlt,
        currentPage = 0
    } = requirementsData || {};
    const { data = [], utilities = {} } = results || {};
    const { filters = [] } = utilities || {};

    const { requirementFilters = {} } = acmData || {};

    const moduleAccessData = useSelector((state) => state.moduleAccess);
    const { moduleAccessMap = {} } = moduleAccessData || {};

    const getFilterTypeOptions = (filters) => {
        let arr = [];
        // eslint-disable-next-line array-callback-return, consistent-return
        filters.forEach((filterTypeObj) => {
            const [firstKey] = Object.keys(filterTypeObj);
            if (firstKey === 'status') {
                arr = [...filterTypeObj[firstKey]];
            }
        });

        return arr;
    };

    const [isLoading, setIsLoading] = useState(true);
    const [toggleButton, setToggleButton] = useState(!isEmptyObject(editableRequirementData));
    const [options, setOptions] = useState([]);

    const handleChangePage = (event, nextPage) => {
        devConsolelog('event', event, nextPage);
        if (nextPage - currentPage > 0) {
            // next
            dispatch(getRequirementsData(next, memberCode, nextPage));
            dispatch(getMemberRequirementEditableData({}));
        } else if (nextPage - currentPage < 0) {
            // previous
            dispatch(getRequirementsData(previous, memberCode, nextPage));
            dispatch(getMemberRequirementEditableData({}));
        }
    };

    devConsolelog('requirements data in member priority ', requirementsData);

    const fetchData = async (viewType = '') => {
        if (viewType === DASHBOARD_VIEW || viewType === MEMBER_LIST_VIEW) {
            const {
                current_status: {
                    current_status: { id: agentReqId = null }
                }
            } = requirementFilters;
            const newAgentFollowUp = agentReqId === 0 ? { next_follow_up_on: requirementFilters?.next_follow_up_on } : requirementFilters;
            await dispatch(getAcmRequirementsData('', newAgentFollowUp));
            handleDrawerOpen();
            await dispatch(
                openSnackbar({
                    open: true,
                    message: 'Requirement Updated',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
        } else {
            await dispatch(getRequirementsData('', memberCode));
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedMember || isEmptyObject(selectedMember) || !isLoading) return;
        fetchData();
    }, [selectedMember]);

    const handleToggleBtn = () => {
        if (toggleButton && !isEmptyObject(editableRequirementData)) {
            dispatch(patchCurrentRequirementData(memberCode, Object.values(editableRequirementData)));
            dispatch(getMemberRequirementEditableData({}));
            if (viewType === VIEW_TYPE_MPD_VIEW) {
                setIsLoading(true);
            }
            setTimeout(() => {
                fetchData(viewType);
            }, 3500);
        }
        setToggleButton((prevState) => !prevState);
    };

    useEffect(() => {
        setOptions([...getFilterTypeOptions(filters)]);
    }, [filters]);

    useEffect(() => {
        if (!memberCode) return;
        dispatch(getMemberStats(memberCode, memberRequirementsFilter, REQUIREMENTS_TYPE));
    }, [memberCode]);

    const handleEditableFields = (reqCode = '', type = '', val = '') => {
        if (type === 'next_follow_up_on') {
            val = `${format(val, 'yyyy-MM-dd')}T00:00:00`;
        }
        const editFieldsCopy = { ...editableRequirementData };
        editFieldsCopy[reqCode] = { ...editFieldsCopy[reqCode], code: reqCode, [type]: val };
        dispatch(getMemberRequirementEditableData(editFieldsCopy));
    };

    const redirectToUrl = (url = '') => {
        window.open(url, '_blank');
    };

    const handleSelectChange = (e) => {
        devConsolelog('handleSelectChange 1234 ', e.target);
        const selectedType = e.target.value;
        let startDate = new Date(Date.now());
        let endDate = new Date(Date.now());
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const item in durationFilterData) {
            devConsolelog('filtermap', item, durationFilterData[item]);
            if (selectedType === item) {
                if (selectedType === 'yesterday') {
                    endDate = new Date(endDate.setDate(endDate.getDate() - durationFilterData.yesterday));
                }
                startDate = new Date(startDate.setDate(startDate.getDate() - durationFilterData[item]));
            }
        }

        devConsolelog('select date', selectedType, startDate, endDate);
        const filtersData = {
            created_at: {
                start_date: format(startDate, 'yyyy-MM-dd'),
                end_date: format(endDate, 'yyyy-MM-dd'),
                isDateFilter: true,
                selectedType
            }
        };
        devConsolelog('send formatteddate for query params', filtersData);

        dispatch(getMemberStats(memberCode, filtersData, REQUIREMENTS_TYPE));
        dispatch(getMemberRequirementsFilter(filtersData));
        // setDurationFilter({ last_created_at: selectedType });
    };

    return (
        <>
            {isLoading ? (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Grid>
            ) : (
                <Grid container item direction="column" spacing={4}>
                    <Grid item xs={12}>
                        <Grid container item spacing={3}>
                            <Grid container item xs={12} alignItems="center" justifyContent="space-between">
                                <Grid xs={2} container item alignItems="center" spacing={0.5}>
                                    <Grid item>
                                        <FilterAltIcon sx={{ color: theme.palette.primary.dark }} />
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="h4" sx={{ fontWeight: 500 }}>
                                            Filters
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid item xs={3}>
                                    <FormControl fullWidth>
                                        <Select
                                            id="requirement_date"
                                            value={createdAt?.selectedType}
                                            onChange={(e) => handleSelectChange(e)}
                                        >
                                            {REQUIREMENT_DURATION_FILTER_OPTIONS.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            {isCountLoading && !isLoading ? (
                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress />
                                </Grid>
                            ) : (
                                <Grid container item spacing={2} xs={12}>
                                    <Grid item xs={3}>
                                        <SubCard className={classes.searchGrid}>
                                            <Grid item container spacing={0.6} xs={12}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.grey[900] }}>
                                                        {allRequirementsCount || '0'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 400, color: theme.palette.text.secondary }}
                                                    >
                                                        All
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </SubCard>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <SubCard className={classes.searchGrid}>
                                            <Grid item container spacing={0.6} xs={12}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.grey[900] }}>
                                                        {holidayRequirementCount || '0'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 400, color: theme.palette.text.secondary }}
                                                    >
                                                        Holidays
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </SubCard>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <SubCard className={classes.searchGrid}>
                                            <Grid item container spacing={0.6} xs={12}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.grey[900] }}>
                                                        {flightRequirementCount || '0'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 400, color: theme.palette.text.secondary }}
                                                    >
                                                        Flights
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </SubCard>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <SubCard className={classes.searchGrid}>
                                            <Grid item container spacing={0.6} xs={12}>
                                                <Grid item xs={12}>
                                                    <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.grey[900] }}>
                                                        {hotelRequirementCount || '0'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 400, color: theme.palette.text.secondary }}
                                                    >
                                                        Hotels
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </SubCard>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid container item spacing={2}>
                        <Grid item container xs={12} justifyContent="space-between" alignItems="center">
                            <Grid item>
                                <Typography variant="h4" sx={{ fontWeight: 500 }}>
                                    Requirement Table
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="text"
                                    disabled={
                                        (isEmptyObject(editableRequirementData) && toggleButton) ||
                                        !moduleAccessMap[MODULE_ACCESS_MEMBER_PRIORITY_DASHBOARD_SIDE_NAV]?.is_active ||
                                        moduleAccessMap[MODULE_ACCESS_MEMBER_PRIORITY_DASHBOARD_SIDE_NAV]?.action.toLowerCase() === 'view'
                                    }
                                    onClick={() => handleToggleBtn()}
                                    sx={{
                                        fontSize: 16,
                                        textDecoration: 'underline',
                                        fontWeight: 500,
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {toggleButton ? 'Save' : 'Edit'}
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <MainCard content={false}>
                                <TableContainer>
                                    <Table sx={{ minWidth: 350 }} aria-label="simple table">
                                        <TableHead>
                                            <TableRow className={classes.primaryBackground}>
                                                <TableHeading />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {isRequirementDataLoading ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center">
                                                        <CircularProgress />
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                data.map((requirement) => {
                                                    const {
                                                        code = '',
                                                        score = '',
                                                        potential = false,
                                                        adults_count: adultCount = 0,
                                                        children_count: childCount = 0,
                                                        departure_at: departureAt = '',
                                                        current_status: currentStatus = {},
                                                        remarks = '',
                                                        updated_at: updatedAt = '',
                                                        next_follow_up_on: followUpDate = '',
                                                        created_at: createdAt = '',
                                                        category = {},
                                                        member_list: memberList = {}
                                                    } = requirement || {};

                                                    const { id: statusId = '' } = currentStatus || {};
                                                    const { name: categoryName = '' } = category || {};
                                                    const { title: destination = '' } = memberList || {};

                                                    return (
                                                        <TableRow
                                                            hover
                                                            key="followUpTable"
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={() => !toggleButton && redirectToUrl(`/requirement-funnel/${code}`)}
                                                        >
                                                            <TableCell sx={{ pl: 4, minWidth: '100px' }} className={classes.stickyTableRow}>
                                                                {code || '-'}
                                                            </TableCell>
                                                            <TableCell sx={{ pl: 4, minWidth: '100px' }}>{score || '-'}</TableCell>
                                                            <TableCell sx={{ pl: 4, minWidth: '100px' }}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            defaultChecked={
                                                                                editableRequirementData[code]?.potential || potential
                                                                            }
                                                                        />
                                                                    }
                                                                    name="potential"
                                                                    disabled={!toggleButton}
                                                                    onChange={(e) => {
                                                                        handleEditableFields(code, 'potential', e.target.checked);
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ pl: 4, minWidth: '120px' }}>{destination || '-'}</TableCell>
                                                            <TableCell sx={{ pl: 4, minWidth: '150px' }}>
                                                                {getFormattedDate(departureAt) || '-'}
                                                            </TableCell>
                                                            <TableCell sx={{ pl: 4 }}>
                                                                <FormControl style={{ width: 200 }}>
                                                                    <Select
                                                                        id="status"
                                                                        key={code}
                                                                        defaultValue={statusId}
                                                                        value={editableRequirementData[code]?.current_status || statusId}
                                                                        disabled={!toggleButton}
                                                                        onChange={(e) => {
                                                                            handleEditableFields(code, 'current_status', e.target.value);
                                                                        }}
                                                                    >
                                                                        {options.map((option) => (
                                                                            <MenuItem value={option?.id}>{option?.name}</MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </TableCell>
                                                            <TableCell sx={{ pl: 4, minWidth: '170px' }}>
                                                                <TextField
                                                                    key={code}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    defaultValue={remarks}
                                                                    value={editableRequirementData[code]?.remarks}
                                                                    disabled={!toggleButton}
                                                                    InputProps={{ disableUnderline: true }}
                                                                    onChange={(e) => handleEditableFields(code, 'remarks', e.target.value)}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ pl: 4, minWidth: '150px' }}>
                                                                {getFormattedDate(updatedAt) || '-'}
                                                            </TableCell>
                                                            <TableCell sx={{ pl: 4, minWidth: '200px' }}>
                                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                    <DesktopDatePicker
                                                                        inputFormat="dd/MM/yyyy"
                                                                        renderInput={(props) => (
                                                                            <TextField fullWidth {...props} helperText="" />
                                                                        )}
                                                                        label="Date"
                                                                        key={code}
                                                                        disabled={!toggleButton}
                                                                        defaultValue={followUpDate}
                                                                        value={
                                                                            editableRequirementData[code]?.next_follow_up_on || followUpDate
                                                                        }
                                                                        onChange={(newValue) => {
                                                                            handleEditableFields(code, 'next_follow_up_on', newValue);
                                                                        }}
                                                                    />
                                                                </LocalizationProvider>
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{ pl: 4, minWidth: '160px' }}
                                                            >{`${adultCount} Adults | ${childCount} Child`}</TableCell>
                                                            <TableCell sx={{ pl: 4, minWidth: '120px' }}>{categoryName || '-'}</TableCell>
                                                            <TableCell sx={{ pl: 4, minWidth: '150px' }}>
                                                                {getFormattedDate(createdAt) || '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    rowsPerPageOptions={[rowsPerPage]}
                                    component="div"
                                    rowsPerPage={rowsPerPage}
                                    page={currentPage}
                                    count={parseInt(count, 10)}
                                    backIconButtonProps={{
                                        'aria-label': 'previous page'
                                    }}
                                    nextIconButtonProps={{
                                        'aria-label': 'next page'
                                    }}
                                    onPageChange={handleChangePage}
                                    labelDisplayedRows={({ from, to, count }) =>
                                        `page ${currentPage + 1} of ${Math.ceil(parseInt(count, 10) / rowsPerPage)}`
                                    }
                                    sx={{ '& .MuiTablePagination-toolbar': { padding: 0 } }}
                                />
                            </MainCard>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </>
    );
};

Requirements.propTypes = {
    handleDrawerOpen: PropTypes.func,
    viewType: PropTypes.bool
};

export default Requirements;
